/**
 * LGFraming experiment translations
 * Last updated: 2025-08-14 14:11:00
 * Updated by: JulianPolly
 */

export const LGFraming = {
    de: {
        title: "Neues Smartphone, neue Versicherung?",
        description: "Wie beeinflusst die Formulierung von Angeboten unsere Entscheidungen? In diesem Experiment geht es um die Frage, ob du dein neues Smartphone gegen Schäden versichern würdest.",
        screens: {
            screen1: {
                title: "Smartphone-Kauf – Eine Entscheidung steht an!",
                content: "Du hast lange gespart und dich gründlich über verschiedene Smartphone-Modelle informiert. Jetzt stehst du im Laden an der Kasse – dein neues, lang ersehntes Gerät liegt bereits vor dir. Kurz bevor du bezahlst, freust du dich schon darauf, es endlich in den Händen zu halten.\n\nAls du an der Reihe bist, fragt dich der Mitarbeiter, ob du eine Versicherung für dein neues Smartphone abschließen möchtest. Diese Versicherung würde Schäden durch Stürze, Glasbrüche und andere Defekte abdecken.",
                employeeIntro: "Der Mitarbeiter sagt:",
                gainFrame: "\"Wenn du eine Versicherung abschließt, ist dein Handy gegen die meisten Schäden abgesichert – zum Beispiel, wenn es dir einmal herunterfällt und das Glas kaputt geht. In dem Fall wird entweder die Reparatur bezahlt oder du erhältst schnell ein Ersatzgerät.\"",
                lossFrame: "\"Wenn du keine Versicherung abschließt, kannst du großes Pech haben, wenn dein Handy herunterfällt. Die Reparatur ist dann sehr kostspielig, und möglicherweise musst du dir sogar ein neues Gerät kaufen.\"",
                question: "Wie wahrscheinlich ist es, dass du eine Versicherung für dein Smartphone abschließt?",
                answer: "Deine Antwort:",
                sliderLabels: [
                    "sehr unwahrscheinlich",
                    "eher unwahrscheinlich",
                    "unentschieden",
                    "eher wahrscheinlich",
                    "sehr wahrscheinlich"
                ]
            },
            results: {
                title: "Ergebnisse: Framing beeinflusst Entscheidungen",
                gainTitle: "Versicherungs-Bewertung (Gain-Frame)",
                lossTitle: "Versicherungs-Bewertung (Loss-Frame)",
                tooltipParticipants: "Teilnehmer",
                loading: "Lade Ergebnisse...",
                userInfo: {
                    group1: "Gruppe 1",
                    group2: "Gruppe 2",
                    advantages: "Vorteile",
                    disadvantages: "Nachteile",
                    forInsurance: "für den Abschluss",
                    againstInsurance: "gegen den Abschluss",
                    intro: "Du warst zufällig in <b>{group}</b> und dir wurden die <b>{framingType}</b> der Entscheidung <b>{direction}</b> einer Versicherung präsentiert bekommen.",
                    noGroup: "Es konnte nicht ermittelt werden, in welcher Gruppe du warst."
                }
            },
            resultsGlobal: {
                title: "Vergleich zu anderen Klassen in deinem Bundesland",
                yourClass: "Deine Klasse",
                otherClasses: "Andere Klassen in Baden-Württemberg",
                allClasses: "Alle Klassen (gesamt)",
                gainFrame: "Gain-Frame",
                lossFrame: "Loss-Frame"
            },
            explanation: {
                title: "Wie Worte unsere Entscheidungen lenken: Gewinn- und Verlustframing",
                content: "Der Framing-Effekt beeinflusst, wie wir Informationen aufnehmen und Entscheidungen treffen.",
                imageAlt: "Serviette mit Notizen",
                sections: {
                    whatIsIt: {
                        title: "Was ist Gewinn- und Verlustframing?",
                        content: "Gewinn- und Verlustframing bedeutet, dass wir dieselbe Information unterschiedlich bewerten, je nachdem, ob sie als Gewinn oder als Verlust dargestellt wird. Die Formulierung beeinflusst, ob wir etwas als positiv oder negativ wahrnehmen – und das steuert unser Verhalten, ohne dass sich an den eigentlichen Fakten etwas ändert."
                    },
                    howItFeels: {
                        title: "Wie fühlt es sich an?",
                        content: "Verluste treffen uns emotional viel stärker als gleich große Gewinne uns freuen. Wenn wir 10 Euro verlieren, ärgert uns das deutlich mehr, als uns das Gewinnen von 10 Euro glücklich macht. Die Freude über Gewinne wächst langsam, aber jeder weitere Verlust schmerzt sofort besonders."
                    },
                    whyWeReact: {
                        title: "Warum reagieren wir so?",
                        content: "Dieses Verhalten nennt man Verlustaversion. Die sogenannte Prospect Theory erklärt, warum: Wir vergleichen alles mit einem Ausgangspunkt, unserem Referenzpunkt. Liegen wir darüber, empfinden wir es als Gewinn – liegen wir darunter, als Verlust. Und weil Verluste für uns besonders schlimm sind, lassen wir uns von der Formulierung schnell beeinflussen – selbst wenn es eigentlich um genau dieselbe Sache geht."
                    },
                    types: {
                        title: "Arten von Framing",
                        content: "Es gibt verschiedene Arten von Framing: Gewinn-Verlust-Framing, Attribut-Framing und Handlungs-Framing."
                    },
                    experiment: {
                        title: "Unser Experiment",
                        content: "In diesem Experiment haben wir untersucht, wie das Framing die Entscheidung zum Abschluss einer Smartphone-Versicherung beeinflusst."
                    },
                    realLife: {
                        title: "Bedeutung im Alltag",
                        content: "Der Framing-Effekt begegnet uns täglich in Werbung, Gesundheitskommunikation und Politik."
                    }
                },
                experiment: {
                    content: "Auch in unserem Experiment habt ihr erlebt, wie unterschiedlich eine Situation durch Framing wirken kann. Euer Referenzpunkt war dabei das neue Smartphone – also der Zustand, dass es noch ganz und unbeschädigt ist. Im Gewinn-Frame wurden vor allem die Vorteile einer Versicherung hervorgehoben. Im Verlust-Frame stand dagegen im Mittelpunkt, welche Nachteile und möglichen Verluste euch drohen, wenn ihr keine Versicherung abschließt."
                },
                sources: {
                    title: "Quellen:",
                    items: [
                        "Tversky, A., & Kahneman, D. (1981). The framing of decisions and the psychology of choice. Science, 211(4481), 453–458.",
                        "Kahneman, D., & Tversky, A. (1984). Choices, values, and frames. American Psychologist, 39(4), 341–350."
                    ]
                }
            }
        }
    },
    en: {
        title: "New Smartphone, New Insurance?",
        description: "How does the wording of offers influence our decisions? This experiment explores whether you would insure your new smartphone against damage.",
        screens: {
            screen1: {
                title: "Smartphone Purchase – A Decision Awaits!",
                content: "You've saved Money for a long time and thoroughly researched various smartphone models. Now, you're at the checkout in the store – your new, long-awaited device is already in front of you. Just before you pay, you can't wait to hold it in your hands.\n\nWhen it's your turn, the employee asks if you would like to purchase insurance for your new smartphone. This insurance would cover damage from drops, broken glass, and other defects.",
                employeeIntro: "The employee says:",
                gainFrame: "\"If you purchase insurance, your phone is protected against most damages – for example, if you drop it once and the glass breaks. In that case, either the repair will be paid for or you'll quickly receive a replacement device.\"",
                lossFrame: "\"If you don't purchase insurance, you could have very bad luck if your phone falls. The repair would then cost you a lot, or you might even have to buy a new device.\"",
                question: "How likely are you to purchase insurance for your smartphone?",
                answer: "Your answer:",
                sliderLabels: [
                    "very unlikely",
                    "somewhat unlikely",
                    "undecided",
                    "somewhat likely",
                    "very likely"
                ]
            },
            results: {
                title: "Results: Framing Influences Decisions",
                gainTitle: "Insurance Assessment (Gain Frame)",
                lossTitle: "Insurance Assessment (Loss Frame)",
                tooltipParticipants: "Participants",
                loading: "Loading results...",
                userInfo: {
                    group1: "Group 1",
                    group2: "Group 2",
                    advantages: "advantages",
                    disadvantages: "disadvantages",
                    forInsurance: "for purchasing",
                    againstInsurance: "against purchasing",
                    intro: "You were randomly in <b>{group}</b> and were presented with the <b>{framingType}</b> of the decision <b>{direction}</b> insurance.",
                    noGroup: "It could not be determined which group you were in."
                }
            },
            resultsGlobal: {
                title: "Comparison to Other Classes in Your State",
                yourClass: "Your Class",
                allClasses: "All classes (overall)",
                otherClasses: "Other Classes in Baden-Württemberg",
                gainFrame: "Gain Frame",
                lossFrame: "Loss Frame"
            },
            explanation: {
                title: "How Words Guide Our Decisions: Gain and Loss Framing",
                content: "The framing effect influences how we process information and make decisions.",
                imageAlt: "Napkin with notes",
                sections: {
                    whatIsIt: {
                        title: "What is Gain and Loss Framing?",
                        content: "Gain and loss framing means that we evaluate the same information differently depending on whether it is presented as a gain or a loss. The wording influences whether we perceive something as positive or negative – and this guides our behavior, without any changes in the actual facts."
                    },
                    howItFeels: {
                        title: "How Does It Feel?",
                        content: "Losses affect us emotionally much stronger than equal-sized gains please us. If we lose 10 euros, it annoys us significantly more than winning 10 euros makes us happy. The joy of gains grows slowly, but each additional loss immediately hurts particularly worse."
                    },
                    whyWeReact: {
                        title: "Why Do We React This Way?",
                        content: "This behavior is called loss aversion. The so-called Prospect Theory explains why: We compare everything with a starting point, our reference point. If we are above it, we perceive it as a gain – if we are below it, we perceive it as a loss. And because losses are particularly bad for us, we are quickly influenced by the wording – even if it's about the exact same thing."
                    },
                    types: {
                        title: "Types of Framing",
                        content: "There are different types of framing: gain-loss framing, attribute framing, and action framing."
                    },
                    experiment: {
                        title: "Our Experiment",
                        content: "In this experiment, we investigated how framing influences the decision to purchase smartphone insurance."
                    },
                    realLife: {
                        title: "Relevance in Everyday Life",
                        content: "The framing effect is present in our daily lives in advertising, health communication, and politics."
                    }
                },
                experiment: {
                    content: "In our experiment, you also experienced how differently a situation can appear through framing. Your reference point was the new smartphone – the state in which it is still new and undamaged. In the gain frame, the advantages of insurance were emphasized. In the loss frame, the focus was on what disadvantages and possible losses you'd have to face if you don't take out insurance."
                },
                sources: {
                    title: "Sources:",
                    items: [
                        "Tversky, A., & Kahneman, D. (1981). The framing of decisions and the psychology of choice. Science, 211(4481), 453–458.",
                        "Kahneman, D., & Tversky, A. (1984). Choices, values, and frames. American Psychologist, 39(4), 341–350."
                    ]
                }
            }
        }
    }
};